import { buildCreateActivityPricingValidationData } from '../createActivityAndPricingConsts';
import NotificationProvider from 'components/NotificationProvider';
import { LOT_OPTIONS } from '../ActivityItemPricing/ActivityItemPricingConsts';
import { generateMissingDefaultPricing } from './pricingUtils';
import { PRICING_MODES } from '../ActivityItemPricing/PricingCalculator/PricingCalculatorConsts';
import { ActivityItemPricingType } from '../../PricingManagementConsts';
import { isStepModified } from './editMappingUtils';


export const createActivitySave = async ({
    stepsData,
    createMutation,
    onSuccess
}) => {
    const validationData = buildCreateActivityPricingValidationData(stepsData);
    const pricingItemsWithDefaults = generateMissingDefaultPricing(validationData?.pricing?.pricingItems || []);

    const pricingItems = pricingItemsWithDefaults.map(item => ({
        ...item,
        clientId: item.isDefaultClientPricing ? null : item.clientId,
        lotId: item.lotOption === LOT_OPTIONS.SPECIFIC_LOTS ? item.selectedLotIds : null,
        price: item.priceConfig.price,
        allLots: item.lotOption === LOT_OPTIONS.ALL_LOTS,
        allTenantLots: item.lotOption === LOT_OPTIONS.ALL_PMF_LOTS,
        priceMode: item.priceConfig.priceMode,
        charge: item.priceConfig.priceMode === PRICING_MODES.CLIENT_PRICE.id ? item.priceConfig.charge : null,
        vendorId: item.vendorIds,
        materialPrices: item.priceConfig?.materialPrices || []
    }));

    const rulesList = (validationData?.rules?.rulesList || []).map(rule => ({
        ...rule,
        lotIds: Array.isArray(rule.lotId) ? rule.lotId.flat() : (rule.lotId != null ? [rule.lotId] : []),
        activityId: 0,
        vehicleTypeIdsList: rule?.vehicleTypeIdsList?.includes(null) ? null : (rule.vehicleTypeIdsList || []),
        ruleParams: (rule.ruleParams || []).filter(param => param?.paramValue),
        calcOnFirstCheckin: rule.calcOnFirstCheckin || false
    }));

    const result = await createMutation({
        activityDetails: { ...validationData.activityDetails, name: validationData.activityDetails?.name?.trim() },
        pricing: pricingItems,
        rules: rulesList
    });

    if (!result?.error) {
        NotificationProvider.success('Activity and pricing created successfully');
        onSuccess();
    } else {
        NotificationProvider.error('Failed to create activity and pricing');
    }
};

export const updateActivitySave = async ({
    stepsData,
    originalSteps,
    activityItemId,
    updateMutation,
    onSuccess
}) => {
    const validationData = buildCreateActivityPricingValidationData(stepsData);
    const activityDetails = validationData.activityDetails;

    // Prepare activity details for update
    const activityDataToSend = {
        ...activityDetails,
        name: activityDetails?.name?.trim(),
        id: activityItemId
    };

    const currentPricingItems = validationData.pricing?.pricingItems || [];

    const pricingItems = currentPricingItems.map(item => {
        const materialPrices = (item.priceConfig?.materialPrices || [])
            .filter(mp => mp.cost > 0)
            .map(mp => ({
                activityItemMaterialId: mp.activityItemMaterialId || null,
                materialIndex: mp.materialIndex,
                cost: mp.cost
            }));

        return {
            activityItemId: activityItemId,
            clientId: item?.isDefaultClientPricing || item?.clientId === null ? null : item?.clientId,
            vendorId: item.vendorIds,
            lotId: item.lotOption === LOT_OPTIONS.SPECIFIC_LOTS ? item.selectedLotIds : null,
            price: item.priceConfig?.price,
            allLots: item.lotOption === LOT_OPTIONS.ALL_LOTS,
            allTenantLots: item.lotOption === LOT_OPTIONS.ALL_PMF_LOTS,
            priceMode: item.priceConfig?.priceMode,
            charge: item.priceConfig?.priceMode === PRICING_MODES.CLIENT_PRICE.id ? item.priceConfig?.charge : null,
            vehicleTypeIdsList: item.vehicleTypeIdsList,
            preferredVendorId: item.preferredVendorId || null,
            materialPrices: materialPrices.length > 0 ? materialPrices : [],
        };
    });

    const shouldRemoveMaterials =
        activityDataToSend.pricingType === ActivityItemPricingType.SIMPLE ||
        !activityDetails.materialsModified;

    if (shouldRemoveMaterials) {
        delete activityDataToSend.materials;
        delete activityDataToSend.deletedMaterialIds;
    }

    delete activityDataToSend.materialsModified;

    // Prepare rules
    const currentRules = validationData.rules?.rulesList || [];
    const rulesList = currentRules.map(rule => ({
        clientId: rule.clientId,
        lotIds: Array.isArray(rule.lotId) ? rule.lotId.flat() : (rule.lotId != null ? [rule.lotId] : []),
        activityId: activityItemId,
        vehicleTypeIdsList: rule.vehicleTypeIdsList?.includes(null) ? null : (rule.vehicleTypeIdsList || []),
        calcOnCheckin: rule.calcOnCheckin || false,
        ruleOperand: rule.ruleOperand,
        daysToComplete: rule.daysToComplete,
        preferredVendorId: rule.preferredVendorId,
        ruleParams: (rule.ruleParams || []).filter((param) => param?.paramValue),
        allLots: rule.allLots || false,
        allTenantLots: rule.allTenantLots || false,
        calcOnFirstCheckin: rule.calcOnFirstCheckin || false,
        timeFrameType: rule.timeFrameType,
    }));

    // Check what changed compared to server data using reliable comparison
    const pricingChanged = isStepModified(originalSteps, stepsData, 1);
    const rulesChanged = isStepModified(originalSteps, stepsData, 2);

    const result = await updateMutation({
        activityDetails: activityDataToSend,
        pricing: pricingItems,
        rules: rulesList,
        pricingChanged,
        rulesChanged
    });

    if (!result?.error) {
        NotificationProvider.success("Activity item updated successfully");
        onSuccess();
    } else {
        NotificationProvider.error("Failed to update activity item");
    }
};
